import cn from 'classnames'
import Button from 'shared/components/button/Button'
import UserCheckIcon from 'shared/icons/UserCheckIcon'
import { UserPlusIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useFollowingUser from 'shared/hooks/useFollowingUser'

type FollowButtonProps = {
  userId: number
  isFollowingInitial: boolean
  // optional props
  onToggleFollowing?: () => void
  dark?: boolean
  fullWidth?: boolean
  className?: string
  textClassName?: string
  followCustomName?: string
  followingCustomName?: string
  hideWhenFollowing?: boolean
  noIcon?: boolean
  fullName?: string
  showIconForMobile?: boolean
}

const FollowButton = (props: FollowButtonProps) => {
  const {
    userId,
    isFollowingInitial,
    dark,
    fullWidth = true,
    className,
    textClassName,
    followCustomName,
    followingCustomName,
    hideWhenFollowing,
    onToggleFollowing,
    noIcon,
    fullName,
    showIconForMobile,
  } = props

  const { isFollowing, isCurrentUser, isLoggedIn, isToggleFollowing, toggleFollowing, isFetchingAuthInfo } =
    useFollowingUser({
      userId,
      isFollowingInitial,
      fullName,
    })

  const handleToggleFollowing = useCallback(async () => {
    await toggleFollowing()
    typeof onToggleFollowing === 'function' && onToggleFollowing()
  }, [onToggleFollowing, toggleFollowing])

  const buttonName = useMemo(() => {
    if (isFollowing) return followingCustomName || 'Following'
    return followCustomName || 'Follow'
  }, [isFollowing, followCustomName, followingCustomName])

  const [isHidden, setIsHidden] = useState(false)
  const shouldHideButton = isFollowing && hideWhenFollowing
  useEffect(() => {
    if (!shouldHideButton) return // do nothing
    setTimeout(() => {
      setIsHidden(true) // hide the following status after 3 seconds
    }, 3000)
  }, [shouldHideButton])

  if (isFetchingAuthInfo) return null // do not show the button if the auth info is fetching
  if (isLoggedIn && isCurrentUser) return null // hide the button if the user is the current user
  if (isHidden) return null // hide the button after follow the user after 3 seconds

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4',
        fullWidth && 'w-full',
        !fullWidth && !showIconForMobile && 'w-[110px]',
        !fullWidth && showIconForMobile && 'w-fit lg:w-[110px]',
      )}
    >
      <Button
        name={buttonName}
        wrapperClass={cn(
          'border w-full text-[0.688rem] lg:text-xs py-1.5 px-2 lg:py-2.5 lg:px-4 text-black',
          dark && 'invert',
          isFollowing ? 'border-transparent bg-transparent' : 'border-black/10 hover:shadow bg-[#191414]/5',
          className,
        )}
        contentClass={cn('!normal-case font-semibold', textClassName)}
        onClick={handleToggleFollowing}
        loading={isToggleFollowing}
        showIconForMobile={showIconForMobile}
      >
        {isToggleFollowing && <span className="inline-block h-6" />}
        {!isToggleFollowing && !noIcon && (
          <span className={cn(!showIconForMobile && 'mr-2', showIconForMobile && 'lg:mr-2')}>
            {!isFollowing && <UserPlusIcon className="size-3 lg:size-5" />}
            {isFollowing && <UserCheckIcon className="size-3 lg:size-5" />}
          </span>
        )}
      </Button>
    </div>
  )
}

export default FollowButton
